import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ago } from "@/lib/watch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ImageSquare } from "@phosphor-icons/react";

// Screenshots need the sign-in header, so they are fetched rather than linked.
function Screenshot({ id }) {
  const [src, setSrc] = useState(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let url = null;
    let live = true;
    api.get(`/feedback/${id}/screenshot`, { responseType: "blob" }).then(
      ({ data }) => {
        url = URL.createObjectURL(data);
        if (live) setSrc(url);
      },
      () => {}
    );
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);
  if (!src) {
    return (
      <div className="w-28 h-20 rounded-lg border border-border flex items-center justify-center text-muted-foreground">
        <ImageSquare size={20} />
      </div>
    );
  }
  return (
    <>
      <button type="button" onClick={() => setBig(true)} className="w-fit">
        <img src={src} alt="Screenshot" className="max-h-40 rounded-lg border border-border" />
      </button>
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="max-w-4xl p-2 bg-surface">
          <DialogTitle className="sr-only">Screenshot</DialogTitle>
          <img src={src} alt="Screenshot" className="w-full h-auto rounded-lg" />
        </DialogContent>
      </Dialog>
    </>
  );
}

function Item({ f, onToggle }) {
  const d = f.device || {};
  return (
    <div
      className={`bg-surface border rounded-2xl p-5 flex flex-col gap-3 ${f.read ? "border-border opacity-70" : "border-brand/50"}`}
      data-testid={`feedback-item-${f.feedback_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold truncate">{f.username || "Unknown player"}</div>
          <div className="text-xs text-muted-foreground truncate">
            {f.email} · {ago(f.created_at)} · {new Date(f.created_at).toLocaleString()}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="rounded-full flex-none" onClick={() => onToggle(f)}>
          {f.read ? "Mark unread" : "Mark read"}
        </Button>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm">{f.message}</p>
      {f.has_screenshot && <Screenshot id={f.feedback_id} />}
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        {f.page && <span>Page: {f.page}</span>}
        {d.platform && <span>{d.platform}</span>}
        {d.window && <span>Window {d.window}</span>}
        {d.screen && <span>Screen {d.screen}</span>}
        {d.theme && <span>{d.theme} theme</span>}
      </div>
      {d.user_agent && <div className="text-[11px] text-muted-foreground/70 break-all">{d.user_agent}</div>}
    </div>
  );
}

export default function FeedbackInbox() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    api.get("/feedback").then(({ data }) => setData(data), () => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && !user.feedback_reader) return <Navigate to="/lobby" replace />;

  const toggle = async (f) => {
    await api.patch(`/feedback/${f.feedback_id}`, { read: !f.read });
    load();
  };

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-10 py-10" data-testid="feedback-inbox">
      <h1 className="font-display text-3xl font-bold tracking-tight">Player feedback</h1>
      <p className="text-muted-foreground mt-1">
        {data ? `${data.items.length} message${data.items.length === 1 ? "" : "s"}, ${data.unread} unread` : "Loading…"}
      </p>
      {error && <p className="mt-6 text-sm text-red-500">Couldn't load feedback.</p>}
      {data && data.items.length === 0 && (
        <p className="mt-10 text-muted-foreground">Nothing yet. Players can send feedback from the menu under their name.</p>
      )}
      <div className="mt-6 flex flex-col gap-4">
        {data?.items.map((f) => <Item key={f.feedback_id} f={f} onToggle={toggle} />)}
      </div>
    </main>
  );
}
